/* import __COLOCATED_TEMPLATE__ from './cancel-subscription.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import moment from 'moment-timezone';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import { SELF_SERVE_CHURN_SURVEY_ID } from 'embercom/lib/billing/surveys';
import launchSurveyAndTrack from 'embercom/lib/onboarding/launch-survey-and-track';

export default class CancelSubscription extends Component {
  @service appService;
  @service router;
  @service intercomEventService;
  @service customerService;
  @service intl;
  @service regionService;
  @tracked isLoseBenefitsModalVisible = false;
  @tracked isCancelQuestionnaireModalVisible = false;
  @tracked isReduceCostsModalVisible = false;
  @tracked showDiscountConfirmationModal = false;
  @tracked isRestartSubscriptionModalOpen = false;

  get canCancelSubscription() {
    return (
      !this.customerService.customer.subscriptionWillBeCancelled &&
      !this.app.isSalesforceContracted &&
      !this.customerService.customer.hasCustomPricing &&
      this.customerService.customer.hasActiveSubscription
    );
  }

  get app() {
    return this.appService.app;
  }

  get isLoseBenefitsModalOpen() {
    return this.isLoseBenefitsModalVisible;
  }

  get isCancelQuestionnaireModalOpen() {
    return this.isCancelQuestionnaireModalVisible;
  }

  get canSeeConfirmCancellationModal() {
    return this.args.redirectSource && !this.customerService.customer.subscriptionWillBeCancelled;
  }

  get isReduceCostsModalOpen() {
    return this.isReduceCostsModalVisible;
  }

  get getPricingModelName() {
    if (this.appService.app.onPricing5) {
      return 'pricing5';
    }
    if (this.appService.app.hasValueBasedPricing2) {
      return 'vbp2';
    }
    if (this.appService.app.hasValueBasedPricing1) {
      return 'vbp1';
    }

    return 'other';
  }

  get planName() {
    if (this.currentSubscriptionPlan && this.currentSubscriptionPlan.nameTranslationKey) {
      return this.intl.t(this.currentSubscriptionPlan.nameTranslationKey);
    }
    return this.intl.t('billing.settings.lose-benefits-modal.cta.plan-name-fallback');
  }

  get currentSubscriptionPlan() {
    let activePlan = this.app.allPlansOnPricingModel.find(
      (plan) => plan.billableCustomerPlan && !plan.product.addon,
    );
    if (!activePlan) {
      return this.customerService.activePlans?.find((plan) => !plan.product.addon); // if billable plan is not found, customer is on a graduating trial plan
    }
    return activePlan;
  }

  get showCancellationBanner() {
    return (
      this.app.canSeeChurnBanner &&
      this.customerService.customer.subscriptionWillBeCancelled &&
      !this.app.inTrial &&
      !this.app.customerHasSucceedingSubscription
    );
  }

  get cancellationBannerCtaText() {
    return 'billing.settings.cancellation-banner.cta.restart';
  }

  get endDate() {
    return moment(this.customerService.customer?.subscription?.currentCycleDate?.to).format('ll');
  }

  get isRestartSubscriptionModalVisible() {
    return (
      this.customerService.customer.subscriptionWillBeCancelled &&
      this.isRestartSubscriptionModalOpen
    );
  }

  @action
  closeLoseBenefitsModal(option, reason = '') {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'lose_benefits_modal_close',
      place: 'settings.subscription.billing.settings',
      section: 'cancel_subscription',
      context: option,
      reason,
    });
    if (option === 'continue_cancellation') {
      // Check if customer is Early Stage - if so, launch survey directly instead of showing reduce costs modal
      if (this.app.validEarlyStageApplicant) {
        this.intercomEventService.trackAnalyticsEvent({
          action: 'clicked',
          object: 'early_stage_survey_triggered',
          place: 'settings.subscription.billing.settings',
          section: 'cancel_subscription',
          context: 'lose_benefits_modal_early_stage_bypass',
        });
        launchSurveyAndTrack(SELF_SERVE_CHURN_SURVEY_ID);
        this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
          queryParams: {
            redirect_source: 'exit_survey',
          },
        });
      } else {
        this.isReduceCostsModalVisible = true;
      }
    }
    // tour_triggered is handled inside the lose-benefits-modal
    // abort_cancellation is just to close the lose-benefits-modal
    this.isLoseBenefitsModalVisible = false;
    if (option === 'tour_skipped') {
      this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
        queryParams: {
          redirect_source: 'exit_survey',
        },
      });
    }
  }

  @action
  closeReduceCostsModal(context) {
    if (!this.app.onPricing5) {
      startSurvey(SELF_SERVE_CHURN_SURVEY_ID);
    }
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'reduce_costs_modal_close',
      place: 'settings.subscription.billing.settings',
      section: 'cancel_subscription',
      context,
    });
    if (
      context === 'churn_discount_applied' ||
      context === 'abort_cancellation' ||
      context === 'tour_triggered'
    ) {
      this.isReduceCostsModalVisible = false;
    } else if (context === 'continue_cancellation') {
      this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
        queryParams: {
          redirect_source: 'cancellation_flow',
        },
      });
    } else if (context === 'tour_skipped') {
      this.isReduceCostsModalVisible = false;
      this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
        queryParams: {
          redirect_source: 'exit_survey',
        },
      });
    }
  }

  @action
  continueCancellation(option) {
    this.isCancelQuestionnaireModalVisible = false;
    if (option === 'dont-want-intercom') {
      this.isLoseBenefitsModalVisible = true;
    } else {
      this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
        queryParams: {
          redirect_source: 'cancellation_flow',
        },
      });
    }
  }

  @action
  closeCancelQuestionnaireModal(option) {
    this.isCancelQuestionnaireModalVisible = false;

    this.isLoseBenefitsModalVisible = option === 'continue_cancellation';
  }

  @action
  closeRestartSubscriptionModal() {
    this.isRestartSubscriptionModalOpen = false;
  }

  @action
  cancelSubscription() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'cancel_subscription_button',
      place: 'settings.subscription.billing.settings',
      section: 'cancel_subscription',
      context: 'billing_settings',
      pricing_model: this.getPricingModelName,
    });
    if (this.app.onFinForPlatforms) {
      this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
        queryParams: {
          redirect_source: 'cancellation_flow',
        },
      });
    } else if (this.regionService.isUS) {
      this.isCancelQuestionnaireModalVisible = true;
    } else {
      this.isLoseBenefitsModalVisible = true;
    }
  }

  @action
  handleBannerCtaClick() {
    this.isRestartSubscriptionModalOpen = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'restart_subscription_banner',
      place: 'settings.subscription.billing.settings',
      section: 'restart_subscription_banner',
      context: 'billing_settings',
    });
  }

  @action
  setConfirmDiscountModalVisibility(visibility) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: visibility ? 'confirm_discount_modal_open' : 'confirm_discount_modal_close',
      section: 'cancel_subscription.reduce_costs_modal.confirm_discount_modal',
      place: 'billing_settings',
    });
    this.showDiscountConfirmationModal = visibility;
  }
}
